import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Navbar from './Navbar';
import Footer from './Footer';
import { useTranslation } from '../contexts/TranslationContext';
import { ChartBarIcon, DocumentArrowDownIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import { Reveal, PageHero } from './LandingShared';

/**
 * MarketReport Component
 * Fetches and displays the latest market reports for the public landing page.
 */
const MarketReport = () => {
    const { t, lang } = useTranslation();
    const [marketReports, setMarketReports] = useState([]);
    const [expandedMonths, setExpandedMonths] = useState(new Set());
    const [selectedReport, setSelectedReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Determine which language to use based on top menu selection
    const reportLanguage = lang === 'es' ? 'espanol' : 'eng';

    useEffect(() => {
        const fetchMarketReports = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await api.get(`/api/public-market-reports`);
                const reports = response.data.reports || [];

                // Group reports by month
                const groupedByMonth = {};
                reports.forEach(report => {
                    const monthKey = report.monthYear ? `${report.monthYear.monthName} ${report.monthYear.year}` : 'Unknown';
                    if (!groupedByMonth[monthKey]) {
                        groupedByMonth[monthKey] = [];
                    }
                    groupedByMonth[monthKey].push(report);
                });

                // Sort months (newest first) and get last 6 months
                const sortedMonths = Object.keys(groupedByMonth)
                    .sort((a, b) => {
                        const [monthA, yearA] = a.split(' ');
                        const [monthB, yearB] = b.split(' ');
                        const monthMap = {
                            January: 1, February: 2, March: 3, April: 4, May: 5, June: 6,
                            July: 7, August: 8, September: 9, October: 10, November: 11, December: 12
                        };
                        if (yearA !== yearB) return parseInt(yearB) - parseInt(yearA);
                        return monthMap[monthB] - monthMap[monthA];
                    })
                    .slice(0, 6);

                const last6Months = sortedMonths.map(month => ({
                    month,
                    reports: groupedByMonth[month]
                }));

                setMarketReports(last6Months);

                // Auto-select first report and expand first month
                if (last6Months.length > 0 && last6Months[0].reports.length > 0) {
                    setExpandedMonths(new Set([last6Months[0].month]));
                    setSelectedReport(last6Months[0].reports[0].reportId);
                }

            } catch (err) {
                console.error('Failed to fetch market reports:', err);
                setError(t('marketReports.error'));
            } finally {
                setLoading(false);
            }
        };

        fetchMarketReports();
    }, [t, lang]);

    // Helper function to translate month names
    const translateMonth = (monthYearString) => {
        if (!monthYearString) return '';
        const parts = monthYearString.split(' ');
        const monthName = parts[0];
        const year = parts.slice(1).join(' ');
        const translatedMonth = t(`marketReports.months.${monthName}`, { defaultValue: monthName });
        return year ? `${translatedMonth} ${year}` : translatedMonth;
    };

    const toggleMonth = (month) => {
        setExpandedMonths(prev => {
            const newSet = new Set(prev);
            if (newSet.has(month)) {
                newSet.delete(month);
            } else {
                newSet.add(month);
            }
            return newSet;
        });
    };

    // Find the currently active report
    const activeReport = marketReports
        .flatMap(m => m.reports)
        .find(report => report.reportId === selectedReport);

    // Download PDF handler
    const handleDownloadPdf = async (report) => {
        try {
            const res = await api.get(`/api/public-market-reports/pdf/${report.reportId}`, {
                responseType: 'blob'
            });
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = report.pdfOriginalName || 'report.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download PDF:', err);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-white">
            <Helmet>
              <title>Market Reports — Cogent Platform</title>
              <meta name="description" content="Access the latest market reports and investment research from Cogent Asset Management. Monthly analysis of global markets, portfolio performance, and economic outlook." />
              <link rel="canonical" href="https://cogentplatform.com/market-reports" />
            </Helmet>
            <Navbar />
            <main className="flex-grow">
                {/* Page Hero Header */}
                <PageHero title={t('marketReports.title')} />

                {/* Content Section */}
                <div className="bg-gradient-to-b from-white to-slate-50 py-12 sm:py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

                            {/* Left Column: Reports Menu */}
                            <div className="lg:col-span-1">
                                <Reveal>
                                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                        {/* Gradient accent line at top */}
                                        <div className="h-1 bg-gradient-to-r from-blue-600 to-cyan-500" />
                                        <div className="p-4">
                                            <h2 className="text-lg font-semibold text-gray-800 mb-4 px-2">{t('marketReports.reports')}</h2>
                                            <div className="text-xs text-gray-500 mb-3 px-2">{t('marketReports.last6Months')}</div>
                                            {loading ? (
                                                <div className="text-center py-4 text-gray-500">{t('marketReports.loading')}</div>
                                            ) : (
                                                <div className="space-y-1">
                                                    {marketReports.map(({ month, reports }) => (
                                                        <div key={month} className="mb-2">
                                                            <button
                                                                onClick={() => toggleMonth(month)}
                                                                className={`w-full text-left px-3 py-2.5 rounded-xl transition-all duration-200 flex items-center justify-between ${
                                                                    expandedMonths.has(month)
                                                                        ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-md'
                                                                        : 'text-gray-700 hover:bg-gray-100'
                                                                }`}
                                                            >
                                                                <span className="font-semibold text-sm">{translateMonth(month)}</span>
                                                                <div className="flex items-center gap-2">
                                                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
                                                                        expandedMonths.has(month)
                                                                            ? 'bg-white/25 text-white'
                                                                            : 'bg-gray-200 text-gray-600'
                                                                    }`}>{reports.length}</span>
                                                                    <span className="text-xs">
                                                                        {expandedMonths.has(month) ? '▼' : '▶'}
                                                                    </span>
                                                                </div>
                                                            </button>
                                                            {expandedMonths.has(month) && (
                                                                <div className="mt-1.5 ml-1 space-y-1">
                                                                    {reports.map((report) => {
                                                                        const isPdf = report.type === 'pdf';
                                                                        const title = isPdf
                                                                            ? report.title
                                                                            : (report[reportLanguage]?.title || report.eng?.title || report.espanol?.title || (lang === 'es' ? 'Sin título' : 'Untitled'));
                                                                        const isSelected = selectedReport === report.reportId;
                                                                        return (
                                                                            <div
                                                                                key={report.reportId}
                                                                                className={`p-2.5 rounded-lg cursor-pointer transition-all duration-150 text-xs ${
                                                                                    isSelected
                                                                                        ? 'bg-blue-50 border-l-2 border-blue-500 pl-3'
                                                                                        : 'hover:bg-gray-50 border-l-2 border-transparent pl-3'
                                                                                }`}
                                                                                onClick={() => setSelectedReport(report.reportId)}
                                                                            >
                                                                                <div className={`font-medium truncate flex items-center gap-1.5 ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                                                                                    {isPdf && <DocumentIcon className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />}
                                                                                    {title}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Reveal>
                            </div>

                            {/* Right Column: Content */}
                            <div className="lg:col-span-3">
                                <Reveal delay={100}>
                                    <div className="bg-white p-8 sm:p-10 rounded-2xl border border-gray-100 shadow-sm min-h-[400px]">
                                        {loading ? (
                                            <div className="animate-pulse space-y-6">
                                                <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                                                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                                                <div className="space-y-3 mt-4">
                                                    <div className="h-4 bg-gray-200 rounded"></div>
                                                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                                                    <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                                                </div>
                                            </div>
                                        ) : error ? (
                                            <div className="text-center text-red-600">{error}</div>
                                        ) : activeReport ? (
                                            <article className="prose max-w-none">
                                                <div className="mb-4">
                                                    <h2 className="flex items-center m-0 text-2xl font-bold text-gray-900">
                                                        {activeReport.type === 'pdf' ? (
                                                            <DocumentIcon className="h-7 w-7 mr-3 text-red-500 flex-shrink-0" />
                                                        ) : (
                                                            <ChartBarIcon className="h-7 w-7 mr-3 text-blue-500 flex-shrink-0" />
                                                        )}
                                                        {activeReport.type === 'pdf'
                                                            ? activeReport.title
                                                            : (activeReport[reportLanguage]?.title || activeReport.eng?.title || activeReport.espanol?.title || (lang === 'es' ? 'Sin título' : 'Untitled'))
                                                        }
                                                    </h2>
                                                </div>

                                                {activeReport.type === 'pdf' ? (
                                                    <div className="space-y-6">
                                                        {/* Download Card - matching Guides/Literature style */}
                                                        <button
                                                            onClick={() => handleDownloadPdf(activeReport)}
                                                            className="group block w-full text-left rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-blue-200 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                                                        >
                                                            <div className="h-1 bg-gradient-to-r from-blue-600 to-cyan-500" />
                                                            <div className="p-6 sm:p-8">
                                                                <div className="flex items-center justify-between">
                                                                    <div className="flex items-center">
                                                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-md">
                                                                            <DocumentIcon className="h-6 w-6 text-white" />
                                                                        </div>
                                                                        <div className="ml-4">
                                                                            <h3 className="text-lg font-bold text-gray-900">
                                                                                {activeReport.title}
                                                                            </h3>
                                                                            <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">PDF</span>
                                                                        </div>
                                                                    </div>
                                                                    <DocumentArrowDownIcon className="h-6 w-6 text-gray-300 group-hover:text-blue-600 group-hover:translate-y-0.5 transition-all duration-300" />
                                                                </div>
                                                            </div>
                                                        </button>

                                                        {/* Executive Summary / Description */}
                                                        {activeReport.description && (
                                                            <div className="bg-slate-50 rounded-xl p-6 border border-gray-100">
                                                                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                                                    <ChartBarIcon className="h-4 w-4 text-blue-500" />
                                                                    {lang === 'es' ? 'Resumen Ejecutivo' : 'Executive Summary'}
                                                                </h3>
                                                                <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                                                                    {activeReport.description}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <>
                                                        {(() => {
                                                            const content = activeReport[reportLanguage]?.content || activeReport.eng?.content || activeReport.espanol?.content || '';
                                                            return content.split('\n\n').filter(Boolean).map((paragraph, index) => (
                                                                <p key={index} className="text-justify mb-6 text-gray-700 leading-relaxed">{paragraph}</p>
                                                            ));
                                                        })()}
                                                    </>
                                                )}
                                            </article>
                                        ) : (
                                            <div className="text-center text-gray-500">
                                                <p>{t('marketReports.noReports')}</p>
                                            </div>
                                        )}
                                    </div>
                                </Reveal>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default MarketReport;
